'use client';

import React, { forwardRef, type PropsWithChildren } from 'react';
import { motion, type MotionProps } from 'framer-motion';
import styles from './Toast.module.css';

export type ToastItemAnimationDisplayProps = PropsWithChildren<
  MotionProps & {
    /**
     * Indicates if the toast instance is in the process of exiting
     */
    isExiting: boolean;
    /**
     * Indicates if the toast instance's placement is at the top of the screen
     */
    isTop: boolean;
    /**
     * Indicates if the toast instance has been dismissed by the user
     */
    isDismissed: boolean;
    /**
     * Callback function to be called when the exit animation is complete
     */
    onRemoveAnimationComplete: () => void;
  }
>;

export const ToastItemAnimationDisplay = forwardRef<
  HTMLDivElement,
  ToastItemAnimationDisplayProps
>(
  (
    {
      isExiting,
      isTop,
      isDismissed,
      onRemoveAnimationComplete,
      children,
      ...otherProps
    },
    forwardedRef
  ) => (
    <motion.div
      ref={forwardedRef}
      className={styles.animatedToastItem}
      data-dismissed-via-drag={isDismissed || undefined}
      data-exiting={isExiting || undefined}
      data-top={isTop || undefined}
      drag="x"
      onAnimationEnd={() => {
        if (isExiting) {
          // dispatch the REMOVE action in the toast state mgmt system when the animation is complete
          onRemoveAnimationComplete();
        }
      }}
      {...otherProps}
    >
      {children}
    </motion.div>
  )
);
